import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Check, AlertCircle, Eye, EyeOff } from "lucide-react";

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // Resend UI (MUST be top-level, not inside conditionals)
  const [resendLoading, setResendLoading] = useState(false);
  const [resendError, setResendError] = useState("");
  const [resendSuccess, setResendSuccess] = useState(false);

  const isTestUser = useMemo(() => {
    try {
      const storedUser = localStorage.getItem("user");
      if (!storedUser) return false;
      const parsed = JSON.parse(storedUser);
      const testUserIds = [13, 14, 15, 16, 17, 18];
      return Boolean(parsed?.id && testUserIds.includes(parsed.id));
    } catch {
      return false;
    }
  }, []);

  // Verify token on mount
  useEffect(() => {
    let alive = true;

    async function verifyToken() {
      setVerifying(true);
      setError("");

      if (!token) {
        if (alive) {
          setValidToken(false);
          setVerifying(false);
          setError("Missing reset token.");
        }
        return;
      }

      try {
        // If you have a dedicated verify endpoint, use it.
        // If not, you can skip verification and just allow submit to handle failures.
        const res = await fetch(`/api/users/verify-reset-token?token=${encodeURIComponent(token)}`, {
          method: "GET",
          credentials: "include",
        });

        if (!alive) return;

        if (res.ok) {
          setValidToken(true);
        } else {
          setValidToken(false);
          // try to read a message, but don’t break if not JSON
          try {
            const data = await res.json();
            setError(data?.error || "This password reset link is invalid or has expired.");
          } catch {
            setError("This password reset link is invalid or has expired.");
          }
        }
      } catch {
        if (!alive) return;
        // If your backend doesn’t have the verify route, this will fail.
        // In that case, allow user to try submit anyway:
        setValidToken(true);
      } finally {
        if (alive) setVerifying(false);
      }
    }

    verifyToken();
    return () => {
      alive = false;
    };
  }, [token]);

  const handleResend = async () => {
    setResendLoading(true);
    setResendError("");
    setResendSuccess(false);

    try {
      const res = await fetch("/api/users/resend-reset-link", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || "Failed to resend reset link");
      }

      setResendSuccess(true);
    } catch (err) {
      setResendError(err?.message || "Failed to resend reset link");
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!token) {
      setError("Missing reset token.");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      setLoading(true);

      const response = await fetch("/api/users/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token, newPassword }),
      });

      if (response.ok) {
        setSuccess(true);
        localStorage.removeItem("user");
        localStorage.removeItem("lastActivity");
        setTimeout(() => navigate("/login"), 3000);
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data?.error || "Failed to reset password");
      }
    } catch (err) {
      console.error("Error:", err);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400 mx-auto mb-4" />
          <p className="text-white/60">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  if (!validToken) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-[#070c1a]/95 backdrop-blur-xl rounded-2xl border border-white/10 shadow-[0_30px_120px_rgba(0,0,0,0.65)] p-8">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 rounded-full border border-red-400/20 bg-red-400/10 flex items-center justify-center mb-4">
              <AlertCircle className="h-8 w-8 text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">Invalid Reset Link</h2>
            <p className="text-white/70 mb-6">
              {error || "This password reset link is invalid or has expired."}
            </p>

            {isTestUser ? (
              <>
                <button
                  onClick={handleResend}
                  disabled={resendLoading || resendSuccess}
                  className="w-full px-6 py-3 rounded-xl bg-gradient-to-r from-yellow-500 to-emerald-500 hover:from-yellow-600 hover:to-emerald-600 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-semibold transition-all disabled:opacity-50 mb-2"
                >
                  {resendLoading ? "Resending..." : resendSuccess ? "Link Sent!" : "Resend Reset Link"}
                </button>

                {resendError ? (
                  <div className="p-2 rounded bg-red-500/10 border border-red-500/20 text-red-400 text-xs mb-2">
                    {resendError}
                  </div>
                ) : null}

                {resendSuccess ? (
                  <div className="p-2 rounded bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs mb-2">
                    Reset link sent to your real email.
                  </div>
                ) : null}

                <p className="text-xs text-white/50 mb-2">If you don't see the email, check your spam folder.</p>
              </>
            ) : (
              <Link
                to="/forgot-password"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white font-semibold transition-all"
              >
                Request New Link
              </Link>
            )}

            <div className="mt-4">
              <Link to="/login" className="text-sm text-white/60 hover:text-white transition-colors">
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-[#070c1a]/95 backdrop-blur-xl rounded-2xl border border-white/10 shadow-[0_30px_120px_rgba(0,0,0,0.65)] p-8">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 rounded-full border border-emerald-400/20 bg-emerald-400/10 flex items-center justify-center mb-4">
              <Check className="h-8 w-8 text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">Password Reset Successfully!</h2>
            <p className="text-white/70 mb-6">Your password has been updated. Redirecting to login...</p>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400 mx-auto" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-[#070c1a]/95 backdrop-blur-xl rounded-2xl border border-white/10 shadow-[0_30px_120px_rgba(0,0,0,0.65)] p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto h-16 w-16 rounded-full border border-emerald-400/20 bg-emerald-400/10 flex items-center justify-center mb-4">
            <Lock className="h-8 w-8 text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Reset Your Password</h2>
          <p className="text-white/60">Enter your new password below</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {error ? (
            <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {error}
            </div>
          ) : null}

          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-white/70 mb-2">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                placeholder="At least 8 characters"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-emerald-400/50 focus:outline-none transition-colors pr-12"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60 transition-colors"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-white/70 mb-2">
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showConfirm ? "text" : "password"}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                placeholder="Confirm your password"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-emerald-400/50 focus:outline-none transition-colors pr-12"
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60 transition-colors"
              >
                {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || !newPassword || !confirmPassword}
            className="w-full px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-semibold transition-all disabled:opacity-50"
          >
            {loading ? "Resetting..." : "Reset Password"}
          </button>

          <Link
            to="/login"
            className="flex items-center justify-center gap-2 text-white/60 hover:text-white transition-colors text-sm"
          >
            Back to Login
          </Link>
        </form>
      </div>
    </div>
  );
}

export default ResetPassword;
